import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { resourceAdaptor } from 'qp-adaptor';
import { createRecommendationService, RecommendationService as LibraryRecommendationService } from 'qp-storefront';
import { AspectRatio, ContainerVm, Resource, ResourceList } from 'qp-ui-interface';
import { getAppVersion, getDeviceType } from 'qp-utility';
import { BehaviorSubject } from 'rxjs';
import { AdobeService } from 'src/app/shared/services/adobe/adobe.service';
import { v4 as uuid } from 'uuid';
import { LocalStorageKeys } from '../../../LocalStorageKeys';
import { ConfigService } from '../../config/config.service';
import { LocalStorageService } from '../../local-storage/local-storage.service';
import { StorefrontPolicyModel } from '../storefront-policy/model/StorefrontPolicyModels';
import { StorefrontPolicyService } from '../storefront-policy/storefront-policy.service';

@Injectable({
  providedIn: 'root',
})
export class RecommendationsService {
  url: string;
  isBrowser = new BehaviorSubject<boolean>(null);
  private recommendationService: LibraryRecommendationService;

  constructor(
    private configService: ConfigService,
    private storefrontPolicyService: StorefrontPolicyService,
    private translate: TranslateService,
    private localStorage: LocalStorageService,
    private adobeService: AdobeService
  ) {
    this.recommendationService = createRecommendationService(this.configService.applicationConfig.recommendURL);
  }

  /**
   * fetch more like this
   *
   * @param contentId
   * @param contentType
   * @returns
   */
  async fetchMoreLikeThis(
    contentId: string,
    contentType: string,
    rating: string[],
    aspectRatio: AspectRatio,
    name = 'content_detail.similarToThis',
    id?: string
  ): Promise<ContainerVm> {
    const resources = (await this.getMoreLikeThis(contentId, contentType, rating))?.resources;
    return this.getAdaptedData(resources, name, aspectRatio, id);
  }

  /**
   * Fetch Popular Shows
   *
   * @returns
   */
  async fetchMostPopular(contentType: string, rating: string[], aspectRatio: AspectRatio): Promise<ContainerVm> {
    const resources = (await this.getMostPopular(contentType, rating))?.resources;
    return this.getAdaptedData(resources, 'content_detail.mostPopular', aspectRatio);
  }

  /**
   * Fetch Most Popular
   *
   * @returns
   */
  async fetchPopular(aspectRatio: AspectRatio): Promise<ContainerVm> {
    const resources = (await this.mostPopular())?.resources;
    return this.getAdaptedData(resources, 'content_detail.mostPopular', aspectRatio);
  }

  /**
   * Fetch Most Popular
   *
   * @returns
   */
  async fetchSearchPopular(aspectRatio: AspectRatio): Promise<ContainerVm> {
    const contentTypes: string[] = this.configService.applicationConfig?.searchContentType?.split(',') ?? [];
    const resources = (await this.getSearchPopular(contentTypes))?.resources;
    return this.getAdaptedData(resources, 'content_detail.mostPopular', aspectRatio);
  }

  /**
   * Fetch Because You Watched
   *
   * @returns
   */
  async fetchBecauseWatched(aspectRatio: AspectRatio): Promise<ContainerVm> {
    const resources = (await this.becauseWatched())?.resources;

    return this.getAdaptedData(resources, 'content_detail.becauseyouWatched', aspectRatio);
  }

  private async mostPopular(): Promise<ResourceList> {
    const request = {
      type: 'most-popular',
      fields: this.getDefaultFields(),
    };

    return this.recommendationService.getRecommendedItems(request, this.getAccessToken());
  }

  private async becauseWatched(): Promise<ResourceList> {
    const request = {
      profileId: this.localStorage.getItem(LocalStorageKeys.SELECTED_PROFILE_ID),
      type: 'because-you-watched',
      fields: this.getDefaultFields(),
    };
    return this.recommendationService.getRecommendedItems(request, this.getAccessToken());
  }

  private async getMostPopular(contentType: string, rating: string[]): Promise<ResourceList> {
    const request = {
      type: 'most-popular',
      fields: [
        ...this.getDefaultFields(),
        { values: [contentType], bias: 1, name: 'cty' },
        { values: [...rating], bias: 1, name: 'log.n' },
      ],
    };
    return this.recommendationService.getRecommendedItems(request, this.getAccessToken());
  }

  private async getMoreLikeThis(contentId: string, contentType: string, rating: string[]): Promise<ResourceList> {
    const request = {
      item: contentId,
      type: 'more-like-this',
      fields: [
        ...this.getDefaultFields(),
        { values: [contentType], bias: 1, name: 'cty' },
        { values: [...rating], bias: 1, name: 'log.n' },
      ],
    };
    return this.recommendationService.getRecommendedItems(request, this.getAccessToken());
  }

  private async getSearchPopular(contentType: string[]): Promise<ResourceList> {
    const request = {
      type: 'most-popular',
      fields: [...this.getDefaultFields(), { values: [...contentType], bias: 1, name: 'cty' }],
    };
    return this.recommendationService.getRecommendedItems(request, this.getAccessToken());
  }

  private getAccessToken() {
    return this.localStorage.getItem(LocalStorageKeys.AUTHDATA) || '';
  }

  private getDefaultFields() {
    const policy: StorefrontPolicyModel = this.storefrontPolicyService.getPolicy(true);
    return [
      { values: [policy.dt], bias: 1, name: 'dt' },
      { values: [policy.reg], bias: 1, name: 'reg' },
      { values: [policy.prd], bias: 1, name: 'prd' },
      { values: [policy.pf], bias: 1, name: 'pf' },
    ];
  }

  private getAdaptedData(resources: Resource[], translateStringName: string, aspectRatio: AspectRatio, id?: string): ContainerVm {
    return {
      id: id ?? uuid(),
      aspectRatio,
      name: this.translate.instant(translateStringName),
      layout: 'carousel',
      resources: !resources
        ? []
        : resources.map((res: Resource) => {
            const resource = resourceAdaptor(
              res,
              this.configService.applicationConfig.imageResizeURL,
              getDeviceType(),
              this.adobeService.fullEntitlementPackages,
              this.localStorage.getItem(LocalStorageKeys.LANG),
              `0-${aspectRatio}`,
              null,
              null,
              {
                containerName: this.translate.instant(translateStringName),
                containerId: this.translate.instant(translateStringName),
              },
              getAppVersion()
            );
            return resource;
          }),
    };
  }
}
